import { useState, useEffect, useRef, useCallback } from "react";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import type { GroupMember } from "@acde/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api";

interface SelectedUser {
  email: string;
  name?: string;
  isMember: boolean;
  isValidating?: boolean;
}

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId: string;
  interviewTitle: string;
  shareToken: string;
  onInvitesSent?: () => void;
}

export function InviteModal({
  isOpen,
  onClose,
  interviewId,
  interviewTitle,
  shareToken,
  onInvitesSent,
}: InviteModalProps) {
  const { authFetch } = useAuthFetch();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GroupMember[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [manualEmail, setManualEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{
    sent: number;
    failed: number;
  } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSearchError(null);
      setSelectedUsers([]);
      setManualEmail("");
      setSendResult(null);
    }
  }, [isOpen]);

  const doSearch = useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      setSearchError(null);
      try {
        const res = await authFetch(
          `${API_URL}/groups/members?q=${encodeURIComponent(query)}`
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.members || []);
        } else if (res.status === 503) {
          setSearchResults([]);
          setSearchError("People search is not available. You can still add emails manually below.");
        } else {
          setSearchResults([]);
          setSearchError("Search failed. You can add emails manually below.");
        }
      } catch {
        setSearchResults([]);
        setSearchError("Search unavailable. You can add emails manually below.");
      } finally {
        setIsSearching(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => doSearch(searchQuery), 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, doSearch]);

  const validateEmail = useCallback(
    async (email: string) => {
      try {
        const res = await authFetch(`${API_URL}/groups/validate-emails`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emails: [email] }),
        });
        if (res.ok) {
          const data = await res.json();
          const result = data.results?.[0];
          return result?.isMember ?? false;
        }
      } catch {
        // If validation fails, assume not a member
      }
      return false;
    },
    [authFetch]
  );

  const addUser = (email: string, name?: string, isMember = true) => {
    const lowerEmail = email.toLowerCase().trim();
    if (!lowerEmail) return;
    if (selectedUsers.some((u) => u.email === lowerEmail)) return;
    setSelectedUsers((prev) => [
      ...prev,
      { email: lowerEmail, name, isMember },
    ]);
    setSearchQuery("");
    setSearchResults([]);
    inputRef.current?.focus();
  };

  const removeUser = (email: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.email !== email));
  };

  const handleManualAdd = async () => {
    const email = manualEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    if (selectedUsers.some((u) => u.email === email)) {
      setManualEmail("");
      return;
    }

    // Add immediately with validating state
    setSelectedUsers((prev) => [
      ...prev,
      { email, isMember: false, isValidating: true },
    ]);
    setManualEmail("");

    // Validate against AD group
    const isMember = await validateEmail(email);
    setSelectedUsers((prev) =>
      prev.map((u) =>
        u.email === email ? { ...u, isMember, isValidating: false } : u
      )
    );
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData("text");
    if (text.includes(";") || text.includes(",")) {
      e.preventDefault();
      const emails = text
        .split(/[;,\n]/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.includes("@"))
        .filter((s) => !selectedUsers.some((u) => u.email === s));

      if (emails.length === 0) return;

      // Add all with validating state
      setSelectedUsers((prev) => [
        ...prev,
        ...emails.map((email) => ({
          email,
          isMember: false,
          isValidating: true,
        })),
      ]);

      // Batch validate
      try {
        const res = await authFetch(`${API_URL}/groups/validate-emails`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emails }),
        });
        if (res.ok) {
          const data = await res.json();
          const resultMap = new Map<string, boolean>();
          for (const r of data.results || []) {
            resultMap.set(r.email, r.isMember);
          }
          setSelectedUsers((prev) =>
            prev.map((u) =>
              resultMap.has(u.email)
                ? { ...u, isMember: resultMap.get(u.email)!, isValidating: false }
                : u
            )
          );
        }
      } catch {
        setSelectedUsers((prev) =>
          prev.map((u) =>
            u.isValidating ? { ...u, isValidating: false } : u
          )
        );
      }
    }
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0 || isSending) return;
    setIsSending(true);
    setSendResult(null);
    try {
      const res = await authFetch(
        `${API_URL}/interviews/${interviewId}/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            emails: selectedUsers.map((u) => u.email),
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        const sent = data.results.filter(
          (r: { status: string }) =>
            r.status === "invited" || r.status === "already_invited"
        ).length;
        const failed = data.results.filter(
          (r: { status: string }) => r.status === "invited_email_failed"
        ).length;
        setSendResult({ sent, failed });
        onInvitesSent?.();
        if (failed === 0) {
          setTimeout(() => onClose(), 2000);
        }
      }
    } catch (err) {
      console.error("Failed to send invitations:", err);
    } finally {
      setIsSending(false);
    }
  };

  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/respond/${shareToken}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const hasInvalidEmails = selectedUsers.some(
    (u) => !u.isMember && !u.isValidating
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex-shrink-0 px-4 pt-4 sm:px-6 sm:pt-6 pb-3 border-b border-border">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-foreground">
              Invite People
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-user-msg transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5 text-muted"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="text-sm text-muted">
            Send email invitations for{" "}
            <strong className="text-foreground">{interviewTitle}</strong>
          </p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 min-h-0">
          {/* Interview info card */}
          <div className="bg-blue-50/80 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2.5">
            <svg
              className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
              Search for Datacom colleagues below or type an email address.
              Recipients will receive an email invitation with a link to start
              the interview.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Search people
            </label>
            <div className="flex items-center gap-2 border border-border rounded-lg bg-card px-3 py-2.5 focus-within:ring-2 focus-within:ring-primary/50 focus-within:border-primary/50">
              <svg
                className="w-4 h-4 text-muted shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onPaste={handlePaste}
                placeholder="Search by name or email..."
                className="flex-1 bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted/60"
              />
              {isSearching && (
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            {/* Search Error */}
            {searchError && searchResults.length === 0 && (
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                {searchError}
              </div>
            )}

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 top-full mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchResults.map((member) => {
                  const alreadySelected = selectedUsers.some(
                    (u) => u.email === member.mail.toLowerCase()
                  );
                  return (
                    <button
                      key={member.id}
                      onClick={() =>
                        addUser(member.mail, member.displayName, true)
                      }
                      disabled={alreadySelected}
                      className="w-full text-left px-3 py-2.5 hover:bg-user-msg transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-semibold text-primary">
                          {member.displayName
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-foreground truncate">
                          {member.displayName}
                        </div>
                        <div className="text-xs text-muted truncate">
                          {member.mail}
                          {member.jobTitle && ` · ${member.jobTitle}`}
                        </div>
                      </div>
                      {alreadySelected && (
                        <svg
                          className="w-4 h-4 text-green-500 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Manual email entry */}
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Or enter an email address
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleManualAdd();
                  }
                }}
                placeholder="name@company.com"
                className="flex-1 px-3 py-2.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 placeholder:text-muted/60"
              />
              <button
                onClick={handleManualAdd}
                disabled={!manualEmail.includes("@")}
                className="px-4 py-2.5 text-sm rounded-lg border border-border font-medium text-foreground hover:bg-user-msg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            <p className="text-[11px] text-muted/70 mt-1.5 px-0.5">
              Tip: paste a list of emails separated by commas or semicolons
            </p>
          </div>

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div>
              <div className="text-sm font-medium text-foreground mb-2">
                {selectedUsers.length} recipient
                {selectedUsers.length !== 1 ? "s" : ""} selected
              </div>
              <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-user-msg/30 p-2.5 max-h-48 overflow-y-auto">
                {selectedUsers.map(({ email, name, isMember, isValidating }) => (
                  <span
                    key={email}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full transition-colors ${
                      isValidating
                        ? "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                        : isMember
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                    }`}
                  >
                    {isValidating ? (
                      <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : !isMember ? (
                      <svg
                        className="w-3 h-3 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-3 h-3 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    )}
                    <span className="max-w-[180px] truncate">
                      {name || email}
                    </span>
                    <button
                      onClick={() => removeUser(email)}
                      className="hover:text-red-500 transition-colors ml-0.5"
                      aria-label={`Remove ${name || email}`}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Warning for non-member emails */}
          {hasInvalidEmails && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2.5">
              <svg
                className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                  Some recipients are not registered
                </p>
                <p className="text-xs text-red-700 dark:text-red-400 leading-relaxed">
                  The highlighted email(s) are not part of the authorised
                  group and won&apos;t be able to log in. An invitation email
                  will still be sent &mdash; please ask them to request access
                  via{" "}
                  <a
                    href="https://selfservice.datacom.co.nz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-red-900 dark:hover:text-red-200"
                  >
                    Activate Self-Service
                  </a>{" "}
                  to join the group.
                </p>
              </div>
            </div>
          )}

          {/* Self-service hint (always shown) */}
          {!hasInvalidEmails && (
            <p className="text-xs text-muted">
              Can&apos;t find someone? They can request access at{" "}
              <a
                href="https://selfservice.datacom.co.nz/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                selfservice.datacom.co.nz
              </a>
            </p>
          )}

          {/* Result message */}
          {sendResult && (
            <div
              className={`px-3 py-2.5 rounded-lg text-sm flex items-center gap-2 ${
                sendResult.failed > 0
                  ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300"
                  : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
              }`}
            >
              <svg
                className={`w-4 h-4 shrink-0 ${sendResult.failed > 0 ? "text-amber-600" : "text-green-600"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    sendResult.failed > 0
                      ? "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z"
                      : "M5 13l4 4L19 7"
                  }
                />
              </svg>
              {sendResult.sent} invitation
              {sendResult.sent !== 1 ? "s" : ""} sent
              {sendResult.failed > 0 &&
                ` · ${sendResult.failed} email${sendResult.failed !== 1 ? "s" : ""} failed`}
            </div>
          )}
        </div>

        {/* Fixed footer */}
        <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row gap-2 p-4 sm:p-6 pt-4 border-t border-border bg-user-msg/30 sm:justify-end">
          <button
            onClick={copyShareLink}
            className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-user-msg transition-colors flex items-center justify-center gap-2 order-2 sm:order-1"
          >
            {copiedLink ? (
              <>
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                Copy Link
              </>
            )}
          </button>
          <button
            onClick={handleSend}
            disabled={selectedUsers.length === 0 || isSending}
            className="w-full sm:w-auto min-w-[10rem] px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm order-1 sm:order-2"
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                Send Invitations
                {selectedUsers.length > 0 && ` (${selectedUsers.length})`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
